import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp, collection, getDocs, runTransaction } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL, deleteObject } from "firebase/storage";
import { db, storage } from "../../firebase";
import AdminShell from "../../components/AdminShell";
import EndorsementLetter from "../../components/EndorsementLetter";
import COE from "../../components/COE";
import Resume from "../../components/Resume";
import DocumentViewer from "../../components/DocumentViewer";
import { buildDocumentData, TEMPLATES, needsDurationAdjustment } from "../../utils/documentTemplates";
import { calcAge, randomAdjustedDuration, formatCertificateNo } from "../../utils/helpers";
import { notifyStatus, notifyRejected } from "../../utils/api";

function Step({ num, title, done, active, locked, children }) {
  const color = done ? "var(--success)" : active ? "var(--gold-500)" : "var(--ink-300)";
  return (
    <div style={{ display: "flex", gap: 16, marginBottom: 24, paddingBottom: 24, borderBottom: "1px solid var(--ink-100)", opacity: locked ? 0.5 : 1 }}>
      <div style={{ width: 36, height: 36, borderRadius: "50%", background: done ? "var(--success)" : active ? "var(--gold-500)" : "var(--ink-100)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, color: done || active ? "white" : "var(--ink-400)", fontWeight: 700, fontSize: 14 }}>
        {done ? <i className="ti ti-check"></i> : num}
      </div>
      <div style={{ flex: 1 }}>
        <h4 style={{ fontSize: 15, marginBottom: 8, color: done ? "var(--success)" : active ? "var(--ink-900)" : "var(--ink-400)" }}>{title}</h4>
        {children}
      </div>
    </div>
  );
}

export default function AdminApplicantDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [a, setA] = useState(null);
  const [hirings, setHirings] = useState([]);
  const [tab, setTab] = useState("info");
  const [docJob, setDocJob] = useState("");
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadName, setUploadName] = useState("");

  const loadApplicant = async () => {
    try {
      const d = await getDoc(doc(db, "applicants", id));
      if (d.exists()) setA({ id: d.id, ...d.data() });
    } catch (e) { console.error(e); }
  };

  useEffect(() => {
    loadApplicant();
    getDocs(collection(db, "hirings")).then((snap) => {
      const withTemplates = snap.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((h) => h.documentTemplate?.company);
      setHirings(withTemplates);
      if (withTemplates.length > 0) setDocJob(withTemplates[0].id);
    }).catch(console.error);
  }, [id]);

  // Applicants who graduated 3+ real years ago (or have no usable graduation
  // date) get a randomized shorter COE duration (2y2m-2y8m), and every COE gets
  // the company's next sequential certificate number for the year. Both are
  // assigned once per job and persisted so they stay stable on every future
  // view/regeneration of the document.
  useEffect(() => {
    if (!a || !docJob) return;
    const needsDuration = needsDurationAdjustment(a) && !a.durationAdjustments?.[docJob];
    const company = (hirings.find((h) => h.id === docJob)?.documentTemplate?.company || "").trim();
    const needsCert = !!company && !a.certificates?.[docJob];
    if (!needsDuration && !needsCert) return;
    const applicantRef = doc(db, "applicants", a.id);
    (async () => {
      let changed = false;
      if (needsDuration) {
        await updateDoc(applicantRef, { [`durationAdjustments.${docJob}`]: randomAdjustedDuration() });
        changed = true;
      }
      if (needsCert) {
        // Transaction so two admins can never take the same number, and an
        // applicant never gets a second number if this runs twice
        const year = new Date().getFullYear();
        const counterRef = doc(db, "counters", `coe-${year}`);
        try {
          await runTransaction(db, async (tx) => {
            const [fresh, counter] = await Promise.all([tx.get(applicantRef), tx.get(counterRef)]);
            if (fresh.data()?.certificates?.[docJob]) return;
            const next = (counter.data()?.[company] || 0) + 1;
            tx.set(counterRef, { [company]: next }, { merge: true });
            tx.update(applicantRef, { [`certificates.${docJob}`]: formatCertificateNo(year, next) });
          });
          changed = true;
        } catch (e) { console.error("Certificate number not assigned:", e); }
      }
      if (changed) loadApplicant();
    })().catch(console.error);
  }, [a, docJob, hirings]);

  const updateApplicant = async (patch, timelineEntry) => {
    setBusy(true);
    try {
      const payload = { ...patch, updatedAt: serverTimestamp() };
      if (timelineEntry) payload.timeline = [...(a.timeline || []), timelineEntry];
      await updateDoc(doc(db, "applicants", a.id), payload);
      await loadApplicant();
      setMsg("Updated.");
    } catch (e) {
      console.error(e); setMsg("Error: " + e.message);
    }
    setBusy(false);
  };

  const acceptApplicant = async () => {
    // Internal review flag only — job selection, schedule, and endorsement
    // processing already started when the applicant submitted their request,
    // so this doesn't unlock or notify anything applicant-facing.
    await updateApplicant(
      { status: "accepted" },
      { stage: "accepted", label: "Reviewed by Admin", date: new Date().toISOString(), done: true }
    );
  };

  const rejectApplicant = async () => {
    if (!rejectReason.trim()) { setMsg("Please enter a rejection reason."); return; }
    setBusy(true);
    try {
      await updateDoc(doc(db, "applicants", a.id), {
        status: "rejected",
        rejectionReason: rejectReason.trim(),
        timeline: [...(a.timeline || []), { stage: "rejected", label: "Application Rejected", date: new Date().toISOString(), done: true, meta: { reason: rejectReason.trim() } }],
        updatedAt: serverTimestamp()
      });
      try { await notifyRejected(a.contactNumber, a.email, `${a.firstName} ${a.lastName}`, rejectReason.trim()); } catch {}
      setRejectReason(""); setShowRejectForm(false);
      await loadApplicant(); setMsg("Application rejected. Applicant notified.");
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };

  const reprocessApplicant = async () => {
    if (!window.confirm("Reprocess this application? It will be moved back to Pending status.")) return;
    await updateApplicant(
      { status: "pending", rejectionReason: "" },
      { stage: "reprocessed", label: "Application Reprocessed — Under Review", date: new Date().toISOString(), done: true }
    );
    try { await notifyStatus(a.contactNumber, a.email, a.firstName, "Your application has been reprocessed and is now under review again. We will notify you of the outcome."); } catch {}
  };

  const setEndorsementStatus = async (status, label, extraPatch = {}) => {
    await updateApplicant(
      { endorsementStatus: status, ...extraPatch },
      { stage: status, label, date: new Date().toISOString(), done: true }
    );
    let smsText = "";
    if (status === "processing") smsText = "The agency is processing your Endorsement. Please wait for SMS or email notification when your documents are ready to receive.";
    if (status === "ready_to_claim") smsText = "Your endorsement documents are ready. Contact your endorser to receive your endorsement and documents and to arrange the pickup date. Payment (₱6,000) is collected in cash upon receipt.";
    if (status === "claimed") smsText = `Thank you and good luck with your career! Please submit your documents to Golden Gateway Agency Office at Don Gil Garcia Street, Capitol Site, Cebu City for slot availability and lineups. The agency will guide you. After successfully hired by the employer through the agency, you may request Company Profile, Brochure, and other Business Documents — go back to the website and use your Endorsement ID ${a.applicationId} for the same process.`;
    if (smsText) {
      try { await notifyStatus(a.contactNumber, a.email, a.firstName, smsText); } catch {}
    }
  };

  // Payment is collected in cash at the moment the applicant receives their
  // documents, so "claimed" and "payment collected" happen as a single action.
  const markPhase1Claimed = () => setEndorsementStatus("claimed", "Endorsement Documents Claimed — Payment Collected", { paymentStatus: "collected" });

  const uploadAttachment = async () => {
    if (!uploadFile) return;
    setBusy(true);
    try {
      const r = ref(storage, `attachments/${a.applicationId}/${Date.now()}-${uploadFile.name}`);
      await uploadBytes(r, uploadFile);
      const url = await getDownloadURL(r);
      const attachments = [...(a.attachments || []), { name: uploadName || uploadFile.name, url, storagePath: r.fullPath, uploadedAt: new Date().toISOString() }];
      await updateDoc(doc(db, "applicants", a.id), { attachments, updatedAt: serverTimestamp() });
      setUploadFile(null); setUploadName(""); await loadApplicant();
    } catch (e) { console.error(e); setMsg("Upload failed"); }
    setBusy(false);
  };

  const setPhase2Status = async (status, label) => {
    setBusy(true);
    try {
      const patch = { phase2Status: status, updatedAt: serverTimestamp() };
      if (status === "claimed") patch["phase2Payment.status"] = "collected";
      await updateDoc(doc(db, "applicants", a.id), {
        ...patch,
        timeline: [...(a.timeline || []), { stage: `phase2_${status}`, label, date: new Date().toISOString(), done: true }]
      });
      if (status === "ready_to_claim") {
        try { await notifyStatus(a.contactNumber, a.email, `${a.firstName} ${a.lastName}`, "Your Company Profile and Brochure documents are ready for pickup at our office. Payment (₱6,000) is collected in cash upon receipt."); } catch {}
      }
      await loadApplicant();
      setMsg("Updated.");
    } catch (e) { setMsg("Error: " + e.message); }
    setBusy(false);
  };

  const deleteAttachment = async (att, idx) => {
    if (!window.confirm(`Delete "${att.name}"?`)) return;
    try {
      if (att.storagePath) {
        try { await deleteObject(ref(storage, att.storagePath)); } catch {}
      }
      const attachments = (a.attachments || []).filter((_, i) => i !== idx);
      await updateDoc(doc(db, "applicants", a.id), { attachments });
      await loadApplicant();
    } catch (e) { console.error(e); }
  };

  if (!a) return <AdminShell><div>Loading...</div></AdminShell>;

  const selectedHiring = hirings.find((h) => h.id === docJob);
  const docTemplate = selectedHiring?.documentTemplate || TEMPLATES[docJob] || null;
  // Use the date the applicant submitted their endorsement request as the document date
  const requestEntry = (a.timeline || []).find((t) => t.stage === "endorsement_requested");
  const docDate = requestEntry?.date ? new Date(requestEntry.date) : (a.createdAt?.toDate?.() || new Date());
  const docData = docTemplate ? buildDocumentData(a, docTemplate, docDate, a.durationAdjustments?.[docJob] || null, a.certificates?.[docJob] || null) : null;

  return (
    <AdminShell title={`${a.firstName} ${a.lastName}`}>
      <div className="card mb-24">
        <div className="flex-between" style={{ flexWrap: "wrap", gap: 12 }}>
          <div>
            <div className="mono text-muted" style={{ fontSize: 12 }}>{a.applicationId}</div>
            <h2 style={{ fontSize: 22 }}>{a.firstName} {a.middleName} {a.lastName}</h2>
            <div className="text-muted">{a.email} · {a.contactNumber}</div>
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <span className={`badge badge-${a.status}`}>{a.status}</span>
            {a.endorsementStatus && <span className={`badge badge-${a.endorsementStatus === "ready_to_claim" ? "ready" : a.endorsementStatus === "claimed" ? "claimed" : "processing"}`}>{a.endorsementStatus.replace("_", " ")}</span>}
            {a.paymentStatus && <span className={`badge ${a.paymentStatus === "collected" ? "badge-paid" : "badge-pending"}`}>{a.paymentStatus.replace(/_/g, " ")}</span>}
          </div>
        </div>
      </div>

      {msg && <div className="alert alert-info">{msg}</div>}

      <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap" }}>
        {["info", "education", "timeline", "documents", "attachments", "actions"].map((t) => (
          <button key={t} className={"btn " + (tab === t ? "btn-primary" : "btn-outline")} onClick={() => setTab(t)} style={{ textTransform: "capitalize" }}>
            {t}
          </button>
        ))}
      </div>

      {tab === "info" && (
        <div className="card">
          <div style={{ display: "grid", gridTemplateColumns: "180px 1fr", gap: 24 }}>
            {a.photoUrl && <img src={a.photoUrl} alt="2x2" style={{ width: 160, height: 160, objectFit: "cover", borderRadius: 8, border: "1px solid var(--ink-200)" }} />}
            <div>
              <table style={{ width: "100%", fontSize: 14 }}>
                <tbody>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Date of Birth</td><td>{a.birthdate}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Age</td><td>{a.age || calcAge(a.birthdate)}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Place of Birth</td><td>{a.placeOfBirth}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Gender</td><td>{a.gender}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Civil Status</td><td>{a.civilStatus}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Citizenship</td><td>{a.citizenship}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Languages</td><td>{(a.languages || []).join(", ")}</td></tr>
                  <tr><td style={{ padding: "6px 0", color: "var(--ink-500)" }}>Address</td><td>{[a.address?.exact, a.address?.barangay, a.address?.city, a.address?.province, a.address?.region].filter(Boolean).join(", ")}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-24">
            <h4 style={{ marginBottom: 8 }}>Professional Objective</h4>
            <p style={{ background: "var(--cream-warm)", padding: 16, borderRadius: 8, fontSize: 14 }}>{a.professionalObjective}</p>
          </div>
          {a.skills?.length > 0 && (
            <div className="mt-16">
              <h4 style={{ marginBottom: 8 }}>Skills</h4>
              <div className="tag-list">{a.skills.map((s) => <span key={s} className="tag">{s}</span>)}</div>
            </div>
          )}
          {a.workExperience?.length > 0 && (
            <div className="mt-16">
              <h4 style={{ marginBottom: 8 }}>Work Experience</h4>
              {a.workExperience.map((w, i) => (
                <div key={i} style={{ padding: 12, background: "var(--cream-warm)", borderRadius: 8, marginBottom: 8 }}>
                  <strong>{w.jobRole}</strong> at {w.company}<br/>
                  <span className="text-muted">{w.startDate} – {w.endDate || "Present"}</span>
                </div>
              ))}
            </div>
          )}
          {a.characterReferences?.length > 0 && (
            <div className="mt-16">
              <h4 style={{ marginBottom: 8 }}>Character References</h4>
              {a.characterReferences.map((r, i) => (
                <div key={i} style={{ padding: 12, background: "var(--cream-warm)", borderRadius: 8, marginBottom: 8 }}>
                  <strong>{r.name}</strong> — {r.position}, {r.company}<br/>
                  <span className="text-muted">{r.contact}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "education" && (
        <div className="card">
          {a.lastGraduation?.year && (
            <div style={{ marginBottom: 14, padding: 12, background: "var(--cream-warm)", borderRadius: 8 }}>
              <div style={{ fontSize: 12, color: "var(--gold-600)", textTransform: "uppercase", letterSpacing: "0.08em" }}>Last Graduated</div>
              <div style={{ fontWeight: 600, fontSize: 16 }}>{a.lastGraduation.month}/{a.lastGraduation.year}</div>
            </div>
          )}
          {["elementary", "secondary", "college"].map((lvl) =>
            a.education?.[lvl]?.schoolName ? (
              <div key={lvl} style={{ marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: "var(--gold-600)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{lvl}</div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{a.education[lvl].schoolName}</div>
                <div className="text-muted">{a.education[lvl].address}</div>
                <div className="text-muted">Graduated: {a.education[lvl].monthGraduated}/{a.education[lvl].yearGraduated}</div>
              </div>
            ) : null
          )}
        </div>
      )}

      {tab === "timeline" && (
        <div className="card">
          <div className="timeline">
            {(a.timeline || []).map((t, i) => (
              <div key={i} className="timeline-item done">
                <div className="timeline-dot"><i className="ti ti-check"></i></div>
                <div className="timeline-title">{t.label}</div>
                <div className="timeline-meta">{t.date ? new Date(t.date).toLocaleString() : ""}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "documents" && (
        <div className="card">
          <div className="flex-between mb-16" style={{ flexWrap: "wrap", gap: 12 }}>
            <h3 style={{ fontSize: 18 }}>Generated Documents</h3>
            {hirings.length > 0 ? (
              <select className="form-control" style={{ maxWidth: 320 }} value={docJob} onChange={(e) => setDocJob(e.target.value)}>
                {hirings.map((h) => (
                  <option key={h.id} value={h.id}>{h.flag} {h.title} — {h.documentTemplate.company}</option>
                ))}
              </select>
            ) : (
              <span className="text-muted" style={{ fontSize: 13 }}>No hirings with document templates. Configure in Hirings page.</span>
            )}
          </div>
          {!docData && hirings.length > 0 && (
            <div className="alert alert-error">Selected hiring has no document template configured.</div>
          )}

          <DocumentViewer title={`Endorsement Letter – ${selectedHiring?.title || docJob}`} fileName={`endorsement-${a.lastName}-${selectedHiring?.title || docJob}`}>
            <EndorsementLetter data={docData} />
          </DocumentViewer>

          <hr style={{ margin: "32px 0", border: "none", borderTop: "1px solid var(--ink-200)" }} />

          <DocumentViewer title={`Certificate of Employment – ${selectedHiring?.title || docJob}`} fileName={`coe-${a.lastName}-${selectedHiring?.title || docJob}`}>
            <COE data={docData} />
          </DocumentViewer>

          <hr style={{ margin: "32px 0", border: "none", borderTop: "1px solid var(--ink-200)" }} />

          <DocumentViewer title="Auto-Generated Resume" fileName={`resume-${a.lastName}`}>
            <Resume applicant={a} />
          </DocumentViewer>
        </div>
      )}

      {tab === "attachments" && (
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 16 }}>Attachments & Files</h3>
          <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
            <input className="form-control" placeholder="File label..." value={uploadName} onChange={(e) => setUploadName(e.target.value)} style={{ maxWidth: 240 }} />
            <input type="file" className="form-control" onChange={(e) => setUploadFile(e.target.files[0])} style={{ maxWidth: 320 }} />
            <button className="btn btn-primary" onClick={uploadAttachment} disabled={!uploadFile || busy}><i className="ti ti-upload"></i> Upload</button>
          </div>

          {(a.attachments?.length || a.paymentReceiptUrl) ? (
            <div>
              {a.paymentReceiptUrl && (
                <div style={{ padding: 14, border: "1px solid var(--ink-200)", borderRadius: 8, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><i className="ti ti-receipt"></i> <strong>GCash Payment Receipt</strong></div>
                  <a href={a.paymentReceiptUrl} target="_blank" rel="noreferrer" className="btn btn-outline"><i className="ti ti-external-link"></i> View</a>
                </div>
              )}
              {(a.attachments || []).map((att, i) => (
                <div key={i} style={{ padding: 14, border: "1px solid var(--ink-200)", borderRadius: 8, marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div><i className="ti ti-file"></i> <strong>{att.name}</strong> <span className="text-muted" style={{ fontSize: 12 }}>· {new Date(att.uploadedAt).toLocaleDateString()}</span></div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <a href={att.url} target="_blank" rel="noreferrer" className="btn btn-outline"><i className="ti ti-eye"></i></a>
                    <button className="btn btn-danger" onClick={() => deleteAttachment(att, i)}><i className="ti ti-trash"></i></button>
                  </div>
                </div>
              ))}
            </div>
          ) : <div className="empty-state"><i className="ti ti-folder-off"></i><div>No attachments</div></div>}
        </div>
      )}

      {tab === "actions" && (
        <div className="card">
          <h3 style={{ fontSize: 18, marginBottom: 4 }}>Admin Actions</h3>
          <p className="text-muted mb-20" style={{ fontSize: 13 }}>Steps must be completed in order. You cannot skip a step.</p>

          {/* STEP 1: Accept / Reject / Reprocess — internal review only, does not gate or notify the applicant */}
          <Step num={1} title="Review Application" done={a.status === "accepted"} active={a.status === "pending"}>
            {a.status === "pending" ? (
              <>
                <p className="text-muted mb-8" style={{ fontSize: 13 }}>Internal review only — the applicant already has their Endorsement ID and their request is already being processed below. Reject only if the request is incomplete or ineligible.</p>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <button className="btn btn-success" onClick={acceptApplicant} disabled={busy}><i className="ti ti-check"></i> Accept</button>
                  <button className="btn btn-danger" onClick={() => setShowRejectForm(!showRejectForm)} disabled={busy}><i className="ti ti-x"></i> Reject</button>
                </div>
                {showRejectForm && (
                  <div style={{ marginTop: 12, padding: 14, background: "var(--cream-warm)", borderRadius: 8, border: "1px solid var(--ink-200)" }}>
                    <label className="form-label">Rejection Reason / Remarks *</label>
                    <textarea className="form-control" rows={3} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} placeholder="Enter the reason for rejection. This will be sent to the applicant via SMS and email." style={{ marginBottom: 8 }} />
                    <div style={{ display: "flex", gap: 8 }}>
                      <button className="btn btn-danger" onClick={rejectApplicant} disabled={busy || !rejectReason.trim()}><i className="ti ti-x"></i> Confirm Reject & Notify</button>
                      <button className="btn btn-outline" onClick={() => { setShowRejectForm(false); setRejectReason(""); }}>Cancel</button>
                    </div>
                  </div>
                )}
              </>
            ) : a.status === "rejected" ? (
              <div>
                <p style={{ fontSize: 13, color: "var(--danger)", marginBottom: 8 }}><i className="ti ti-x"></i> Rejected{a.rejectionReason ? ` — "${a.rejectionReason}"` : ""}.</p>
                <button className="btn btn-outline" onClick={reprocessApplicant} disabled={busy}><i className="ti ti-refresh"></i> Reprocess Application</button>
              </div>
            ) : (
              <p style={{ fontSize: 13, color: "var(--success)" }}><i className="ti ti-check"></i> Reviewed (internal record only).</p>
            )}
          </Step>

          {/* STEP 2: Phase 1 — Endorsement progress. Payment is collected in cash when the
               applicant receives their documents, so it's folded into "Mark as Claimed" below. */}
          {(() => {
            const step2Done = ["claimed", "for_aboard"].includes(a.endorsementStatus);
            return (
              <Step num={2} title="Phase 1 — Endorsement Documents" done={step2Done} active={!step2Done}>
                <p className="text-muted mb-12" style={{ fontSize: 13 }}>
                  Current status: <strong>{a.endorsementStatus || "not started"}</strong>
                  {a.selectedJobs?.length > 0 && <> · Jobs: <strong>{a.selectedJobs.join(", ")}</strong></>}
                  {a.claimDate && <> · Preferred pickup: <strong>{new Date(a.claimDate).toLocaleString()}</strong></>}
                </p>
                {step2Done ? (
                  <p style={{ fontSize: 13, color: "var(--success)" }}><i className="ti ti-check"></i> Endorsement documents claimed — payment collected.</p>
                ) : (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {!a.endorsementStatus && (
                      <button className="btn btn-outline" onClick={() => setEndorsementStatus("processing", "Endorser is Processing Endorsement")} disabled={busy}>
                        <i className="ti ti-loader"></i> Mark as Processing
                      </button>
                    )}
                    {a.endorsementStatus === "processing" && (
                      <button className="btn btn-primary" onClick={() => setEndorsementStatus("ready_to_claim", "Endorsement Ready to Claim")} disabled={busy}>
                        <i className="ti ti-package"></i> Mark as Ready to Claim
                      </button>
                    )}
                    {a.endorsementStatus === "ready_to_claim" && (
                      <button className="btn btn-success" onClick={markPhase1Claimed} disabled={busy}>
                        <i className="ti ti-cash"></i> Mark as Claimed (Payment Collected)
                      </button>
                    )}
                  </div>
                )}
              </Step>
            );
          })()}

          {/* STEP 3: Phase 2 — only shows once the applicant has requested it via Track Endorsement */}
          {a.phase2Status && (
            <Step num={3} title="Phase 2 — Company Profile, Brochure &amp; Business Documents" done={a.phase2Status === "claimed"} active={a.phase2Status !== "claimed"}>
              <p className="text-muted mb-8" style={{ fontSize: 13 }}>
                Current: <strong>{a.phase2Status}</strong> · Job: <strong>{a.phase2Job}</strong>
                {a.phase2ClaimDate && <> · Preferred pickup: <strong>{new Date(a.phase2ClaimDate).toLocaleString()}</strong></>}
              </p>
              {a.phase2Status === "claimed" ? (
                <p style={{ fontSize: 13, color: "var(--success)" }}><i className="ti ti-check"></i> Phase 2 documents claimed — payment collected.</p>
              ) : (
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {a.phase2Status === "processing" && <button className="btn btn-primary" onClick={() => setPhase2Status("ready_to_claim", "Company Profile & Brochure Ready to Claim")} disabled={busy}>Mark as Ready to Claim</button>}
                  {a.phase2Status === "ready_to_claim" && <button className="btn btn-success" onClick={() => setPhase2Status("claimed", "Company Profile & Brochure Claimed")} disabled={busy}><i className="ti ti-cash"></i> Mark as Claimed (Payment Collected)</button>}
                </div>
              )}
            </Step>
          )}

          {/* FINAL: For Aboard
               Shows after Phase 1 claimed.
               If Phase 2 was requested, must wait until Phase 2 is also claimed (all phases complete). */}
          {a.endorsementStatus === "claimed" && (
            (() => {
              const phase2Requested = !!a.phase2Status;
              const phase2Complete = a.phase2Status === "claimed";
              const allPhasesComplete = !phase2Requested || phase2Complete;
              const stepNum = phase2Requested ? 4 : 3;
              return (
                <Step num={stepNum} title="Final — For Aboard (All Phases Complete)" done={a.endorsementStatus === "for_aboard"} active={allPhasesComplete && a.endorsementStatus !== "for_aboard"} locked={!allPhasesComplete}>
                  {a.endorsementStatus === "for_aboard" ? (
                    <div className="alert alert-success" style={{ margin: 0 }}>
                      <i className="ti ti-plane"></i> <strong>For Aboard</strong> — All phases complete. Applicant is cleared and ready for overseas employment.
                    </div>
                  ) : !allPhasesComplete ? (
                    <div>
                      <p className="text-muted" style={{ fontSize: 13 }}>
                        Phase 2 (Company Profile, Brochure &amp; Business Documents) has been requested and must be completed first.
                      </p>
                      <p className="text-muted mt-4" style={{ fontSize: 13 }}>
                        Current Phase 2 status: <strong>{a.phase2Status?.replace(/_/g, " ") || "pending"}</strong>
                      </p>
                    </div>
                  ) : (
                    <>
                      <p className="text-muted mb-8" style={{ fontSize: 13 }}>
                        {phase2Requested
                          ? "All phases complete — Phase 1 (Endorsement) and Phase 2 (Company Profile & Business Documents) are both claimed."
                          : "Phase 1 (Endorsement Documents) is claimed. Mark applicant as cleared for overseas employment."}
                      </p>
                      <button className="btn btn-success" onClick={() => updateApplicant(
                        { endorsementStatus: "for_aboard" },
                        { stage: "for_aboard", label: "Cleared — For Aboard", date: new Date().toISOString(), done: true }
                      )} disabled={busy}>
                        <i className="ti ti-plane"></i> Mark For Aboard
                      </button>
                    </>
                  )}
                </Step>
              );
            })()
          )}
        </div>
      )}
    </AdminShell>
  );
}
